import { useEffect, useRef } from 'react';

const industries = [
  {
    icon: 'ri-heart-pulse-line',
    sector: 'Healthcare',
    title: 'Clinical Practices',
    description:
      'Patient intake automation, scheduling intelligence, and compliance-safe documentation workflows. Built to reduce administrative burden while maintaining clinical precision.',
    capabilities: ['Automated patient intake', 'Scheduling system optimization', 'Compliance-safe workflows', 'Missed appointment recovery'],
    img: 'https://public.readdy.ai/ai/img_res/bf9e00a5c1d9cbaa661398691b2066ff.jpg',
  },
  {
    icon: 'ri-building-2-line',
    sector: 'Construction',
    title: 'Project Delivery',
    description:
      'Lead-to-job pipeline management, project tracking dashboards, and field-to-office coordination systems. Eliminate communication gaps that delay delivery and erode margin.',
    capabilities: ['Lead-to-job pipeline', 'Project tracking systems', 'Field-to-office coordination', 'Subcontractor management'],
    img: 'https://readdy.ai/api/search-image?query=aerial%20view%20modern%20commercial%20construction%20site%20with%20organized%20steel%20framework%20and%20professional%20cranes%2C%20dramatic%20golden%20hour%20sunlight%2C%20premium%20construction%20photography%2C%20clean%20organized%20worksite%2C%20no%20text&width=800&height=400&seq=ind-construction-v1&orientation=landscape',
  },
  {
    icon: 'ri-line-chart-line',
    sector: 'Financial Services',
    title: 'Institutional Finance',
    description:
      'Deal flow management, CRM intelligence, and pipeline automation for institutional financial organizations that require structured oversight and data integrity.',
    capabilities: ['Deal flow management', 'CRM intelligence layers', 'Pipeline automation', 'Investor reporting systems'],
    img: 'https://readdy.ai/api/search-image?query=sleek%20modern%20financial%20institution%20interior%20with%20multiple%20trading%20screens%20showing%20market%20data%2C%20dark%20sophisticated%20ambient%20lighting%2C%20premium%20financial%20technology%20environment%2C%20ultra%20clean%20minimal%20aesthetic%2C%20no%20text&width=800&height=400&seq=ind-finance-v1&orientation=landscape',
  },
  {
    icon: 'ri-file-list-3-line',
    sector: 'Lending & Advisory',
    title: 'Advisory Firms',
    description:
      'Intake, qualification, and automated follow-up systems for lending and advisory organizations. Convert more inquiries into qualified opportunities without adding headcount.',
    capabilities: ['Intake qualification systems', 'Automated follow-up sequences', 'Compliance documentation', 'Pipeline visibility tools'],
    img: 'https://readdy.ai/api/search-image?query=elegant%20premium%20business%20advisory%20office%20with%20sleek%20conference%20table%2C%20large%20windows%20with%20city%20view%2C%20luxury%20interior%20design%2C%20sophisticated%20professional%20environment%2C%20warm%20neutral%20tones%2C%20no%20people%2C%20no%20text&width=800&height=400&seq=ind-lending-v1&orientation=landscape',
  },
];

export default function IndustriesSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const items = entry.target.querySelectorAll('[data-animate]');
            items.forEach((item, i) => {
              setTimeout(() => {
                (item as HTMLElement).style.opacity = '1';
                (item as HTMLElement).style.transform = 'translateY(0)';
              }, i * 100);
            });
          }
        });
      },
      { threshold: 0.06 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="industries" className="bg-[#FBFBFD] dark:bg-[#1A1A1D] py-28 md:py-40 border-t border-[#E8E8ED] dark:border-[#252528]">
      <div className="max-w-7xl mx-auto px-6 md:px-12" ref={sectionRef}>
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-20">
          <div className="inline-flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-[#5BBFB5]" />
            <span className="text-[#C9A840] text-xs font-semibold" style={{ letterSpacing: '0.22em', textTransform: 'uppercase' }}>
              Industries Served
            </span>
            <span className="w-8 h-px bg-[#5BBFB5]" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#1D1D1F] dark:text-[#F2F0EC] tracking-tight leading-tight mb-6">
            Built for High-Stakes<br />
            <span className="text-[#C9A840]">Operating Environments</span>
          </h2>
          <p className="text-[#6E6E73] dark:text-[#96969E] text-lg leading-relaxed">
            Auditore AI operates exclusively within industries where the margin for operational
            error is narrow and the cost of inaction is measurable.
          </p>
        </div>

        {/* Industry cards — 2x2 grid with images */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {industries.map((ind, i) => (
            <div
              key={ind.title}
              data-animate
              style={{
                opacity: 0,
                transform: 'translateY(24px)',
                transition: `opacity 0.7s ease ${i * 0.1}s, transform 0.7s ease ${i * 0.1}s`,
              }}
              className="group bg-white dark:bg-[#0B0B0C] border border-[#E8E8ED] dark:border-[#252528] hover:border-[#C9A840]/40 dark:hover:border-[#C9A840]/30 overflow-hidden transition-all duration-400"
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={ind.img}
                  alt={ind.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-white/60 dark:from-[#0B0B0C]/80 via-transparent to-transparent" />
                {/* Sector label overlay */}
                <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-[#0B0B0C]/70 backdrop-blur-sm px-3 py-1.5">
                  <i className={`${ind.icon} text-[#C9A840] text-xs`}></i>
                  <span className="text-[#F2F0EC] text-xs font-medium" style={{ letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                    {ind.sector}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-8">
                <h3 className="text-[#1D1D1F] dark:text-[#F2F0EC] text-xl font-bold tracking-tight mb-4">
                  {ind.title}
                </h3>
                <p className="text-[#6E6E73] dark:text-[#96969E] text-sm leading-relaxed mb-7">
                  {ind.description}
                </p>
                <div className="border-t border-[#E8E8ED] dark:border-[#252528] pt-5">
                  <ul className="grid grid-cols-2 gap-2">
                    {ind.capabilities.map((cap) => (
                      <li key={cap} className="flex items-center gap-2 text-xs text-[#6E6E73] dark:text-[#96969E]">
                        <span className="w-1 h-1 bg-[#C9A840] rounded-full flex-shrink-0" />
                        {cap}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
